import React from 'react';
import axios from 'axios';
import { Form, Button, Alert } from 'react-bootstrap';
import FormContainer from '../components/FormContainer';
import ReCAPTCHA from 'react-google-recaptcha';

// sanitation & validation helpers
const MAX_SUBJECT = 150;
const MAX_MESSAGE = 2500;
// eslint-disable-next-line no-control-regex -- intentional: strips ASCII control chars from input
const STRIP_CTRL_REGEX = /[\u0000-\u001F\u007F]/g;
const HTML_TAG_REGEX = /<[^>]+>/;
const SCRIPT_TAG_REGEX = /<\s*script\b[\s\S]*?<\s*\/\s*script\s*>/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function stripCtrl(v=''){ return v.replace(STRIP_CTRL_REGEX,''); }
function removeNewlines(v=''){ return v.replace(/\r\n?|\n/g,' ');} 
function collapseWS(v=''){ return v.replace(/\s{2,}/g,' ');} 
function sanitize(v=''){ v=(v||'').toString().normalize('NFC'); v=stripCtrl(v); v=removeNewlines(v); v=collapseWS(v); return v.trim(); }
function hasProhibited(v=''){ return SCRIPT_TAG_REGEX.test(v) || HTML_TAG_REGEX.test(v); }

function ContactUs() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [charCount, setCharCount] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(null);
  const [captchaToken, setCaptchaToken] = React.useState('');
  const captchaRef = React.useRef(null);

  const handleMessageChange = (e) => {
    let v = e.target.value;
    if (v.length > MAX_MESSAGE) v = v.slice(0, MAX_MESSAGE);
    setMessage(v); setCharCount(v.length); if (error) setError(null); };

  const onCaptchaChange = (val) => { setCaptchaToken(val || ''); if (error) setError(null); };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true); setError(null); setSuccess(null);

    const sName = sanitize(name).slice(0,80);
    const sEmail = sanitize(email).toLowerCase();
    const sSubject = sanitize(subject).slice(0, MAX_SUBJECT);
    const sMessage = sanitize(message).slice(0, MAX_MESSAGE);

    if (!sName || !sEmail || !sSubject || !sMessage) { setError('All fields are required'); setSubmitting(false); return; }
    if (!EMAIL_REGEX.test(sEmail)) { setError('Invalid email'); setSubmitting(false); return; }
    if (hasProhibited(sSubject) || hasProhibited(sMessage)) { setError('HTML/script not allowed'); setSubmitting(false); return; }
    if (!captchaToken && process.env.REACT_APP_CONTACT_REQUIRE_CAPTCHA !== 'false') { setError('Captcha required'); setSubmitting(false); return; }

    try {
      await axios.post('/api/contact', { name: sName, email: sEmail, subject: sSubject, message: sMessage, grecaptcha: captchaToken });
      setSuccess('Your message was successfully sent!');
      setName(''); setEmail(''); setSubject(''); setMessage(''); setCharCount(0); setCaptchaToken('');
      if (captchaRef.current) captchaRef.current.reset();
    } catch (err) {
      setError(err?.response?.data?.message || 'An error occurred while submitting the form');
    } finally { setSubmitting(false); }
  };

  const disableSubmit = submitting || !name.trim() || !email.trim() || !subject.trim() || !message.trim() || (!captchaToken && process.env.REACT_APP_CONTACT_REQUIRE_CAPTCHA !== 'false');

  return (
    <FormContainer>
      <h1>Contact Us</h1>
      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group controlId="formName" className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control type="text" placeholder="Enter your name" value={name} onChange={e=>setName(e.target.value)} />
        </Form.Group>
        <Form.Group controlId="formEmail" className="mb-3">
          <Form.Label>Email address</Form.Label>
          <Form.Control type="email" placeholder="Enter your email" value={email} onChange={e=>setEmail(e.target.value)} />
        </Form.Group>
        <Form.Group controlId="formSubject" className="mb-3">
          <Form.Label>Subject</Form.Label>
          <Form.Control type="text" placeholder="Subject" maxLength={MAX_SUBJECT} value={subject} onChange={e=>setSubject(e.target.value)} />
          <div className="small text-muted text-end">{subject.length}/{MAX_SUBJECT}</div>
        </Form.Group>
        <Form.Group controlId="formMessage" className="mb-3">
          <Form.Label>Message</Form.Label>
          <Form.Control as="textarea" rows={5} placeholder="Enter your message" value={message} onChange={handleMessageChange} />
          <div className="small text-muted text-end">{charCount}/{MAX_MESSAGE}</div>
        </Form.Group>
        <div className="mb-3">
          <ReCAPTCHA ref={captchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''} onChange={onCaptchaChange} />
        </div>
        {error && <Alert variant="danger" className="py-2" role="alert">{error}</Alert>}
        {success && <Alert variant="success" className="py-2" role="status" aria-live="polite">{success}</Alert>}
        <Button variant="primary" type="submit" disabled={disableSubmit}>{submitting ? 'Submitting...' : 'Submit'}</Button>
      </Form>
    </FormContainer>
  );
}

export default ContactUs;